import React, { type FunctionComponent } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import classNames from 'classnames';
import BarGraphIcon from 'components/Icons/BarGraphIcon';
import type { ObservationType } from 'types/surf';
import ObservationIcon from './ObservationIcon';

import styles from './GraphLegend.module.scss';

interface LegendItemProps {
  icon: React.ReactNode;
  label: string;
  textClassName: string;
  dataTestId?: string;
  isActive?: boolean;
}

const LegendItem: FunctionComponent<LegendItemProps> = ({
  icon,
  label,
  textClassName,
  dataTestId,
  isActive = true,
}) => (
  <Stack
    alignItems="center"
    className={classNames({
      [styles.legendItem]: true,
      [styles.inactive]: !isActive,
    })}
    data-testid={dataTestId}
    direction="row"
    spacing={0.5}
  >
    <Box className={styles.icon}>{icon}</Box>
    <Typography variant="caption1" className={classNames(textClassName)}>
      {label}
    </Typography>
  </Stack>
);

const getObservationLabel = (type: ObservationType): string => {
  if (type === 'MULTIPLE') return 'Observed';
  if (type === 'CAMERA') return 'Observed by Smart Cam';
  return 'Observed by Forecaster';
};

interface GraphLegendProps {
  dataTestId?: string;
  observationType?: ObservationType | null;
  hasActiveObservation?: boolean;
}

const GraphLegend: FunctionComponent<GraphLegendProps> = ({
  dataTestId = 'forecast-graph-legend',
  observationType = null,
  hasActiveObservation = false,
}) => {
  const forecastIconClassName = classNames({
    [styles.forecastIcon]: true,
    [styles.inactive]: hasActiveObservation,
  });

  const forecastTextClassName = classNames({
    [styles.forecastText]: true,
    [styles.inactive]: hasActiveObservation,
  });

  const observationIconClassName = classNames({
    [styles.observationIcon]: true,
    [styles.inactive]: !hasActiveObservation,
  });

  const observationTextClassName = classNames({
    [styles.observationText]: true,
    [styles.inactive]: !hasActiveObservation,
  });

  return (
    <Box className={styles.legendContainer} data-testid={dataTestId}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <LegendItem
          icon={
            <BarGraphIcon className={forecastIconClassName} clipPathId="legend-bar-graph-icon" />
          }
          textClassName={forecastTextClassName}
          label="LOTUS Forecast"
          dataTestId={`${dataTestId}-lotus-forecast`}
          isActive={!hasActiveObservation}
        />
        {observationType && (
          <LegendItem
            icon={<ObservationIcon type={observationType} className={observationIconClassName} />}
            label={getObservationLabel(observationType)}
            textClassName={observationTextClassName}
            dataTestId={`${dataTestId}-observed-${observationType}`}
            isActive={hasActiveObservation}
          />
        )}
      </Stack>
    </Box>
  );
};

export default GraphLegend;
